import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  GraduationCap,
  HeartHandshake,
  FlaskConical,
  Megaphone,
  ClipboardCheck,
  ChevronRight,
} from "lucide-react";

const SERVICES = [
  {
    icon: GraduationCap,
    title: "Capacitación Laboral Inclusiva",
    description:
      "Programas privados de formación técnica y eficiencia operativa diseñados especialmente para personas desempleadas, subempleadas o en situación de discapacidad.",
    highlights: [
      "Formación técnica especializada",
      "Eficiencia operativa",
      "Inclusión de personas desempleadas, subempleadas o con discapacidad",
    ],
  },
  {
    icon: HeartHandshake,
    title: "Orientación Social y Desarrollo de Talento",
    description:
      "Servicios profesionales combinados de apoyo en instalaciones, tutoría y programas de recursos humanos enfocados en la retención e integración de talento diverso.",
    highlights: [
      "Apoyo en instalaciones y tutoría",
      "Programas de recursos humanos",
      "Retención e integración de talento diverso",
    ],
  },
  {
    icon: FlaskConical,
    title: "Investigación en Ciencias Sociales",
    description:
      "Estudios avanzados, encuestas de opinión pública y análisis de mercado con base científica para evaluar el impacto social y viabilidad de los proyectos comerciales.",
    highlights: [
      "Estudios avanzados y encuestas de opinión pública",
      "Análisis de mercado con base científica",
      "Evaluación de impacto social y viabilidad comercial",
    ],
  },
  {
    icon: Megaphone,
    title: "Consultoría en Expansión y Comercialización",
    description:
      "Asesoría especializada en desarrollo de negocios, optimización de canales de distribución omnicanal e implementación de plataformas de e-commerce y marketing digital.",
    highlights: [
      "Desarrollo de negocios",
      "Canales de distribución omnicanal",
      "E-commerce y marketing digital",
    ],
  },
  {
    icon: ClipboardCheck,
    title: "Auditoría de Procesos y Gestión de Riesgos",
    description:
      "Evaluaciones detalladas de eficiencia empresarial, auditorías operativas y desarrollo de planes de contingencia para garantizar la continuidad y seguridad de tu negocio.",
    highlights: [
      "Evaluaciones de eficiencia empresarial",
      "Auditorías operativas",
      "Planes de contingencia y continuidad",
    ],
  },
];

export const Services = () => {
  const [active, setActive] = useState(0);

  return (
    <section
      id="servicios"
      data-testid="services-section"
      className="py-24 lg:py-32 bg-slate-50"
    >
      <div className="container-page">
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.25em] text-[#055794] font-semibold mb-5">
              Nuestros servicios
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#022140] tracking-tight leading-[1.05] text-balance">
              Soluciones empresariales diseñadas para tu crecimiento.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 flex items-end">
            <p className="text-[17px] text-[#7F7F7F] leading-relaxed">
              Cinco áreas estratégicas, decenas de soluciones especializadas.
              Selecciona una categoría para conocer todo lo que podemos hacer
              por tu empresa.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Tab list */}
          <div className="lg:col-span-5" data-testid="services-tablist">
            <div className="space-y-2">
              {SERVICES.map((s, i) => {
                const Icon = s.icon;
                const isActive = i === active;
                return (
                  <button
                    key={s.title}
                    onClick={() => setActive(i)}
                    data-testid={`service-tab-${i}`}
                    className={`w-full text-left flex items-center gap-4 p-5 rounded-2xl border transition-all duration-300 ${
                      isActive
                        ? "bg-[#022140] border-[#022140] text-white shadow-lg"
                        : "bg-white border-slate-200 text-[#022140] hover:border-[#72B3E4] hover:-translate-y-0.5"
                    }`}
                  >
                    <span
                      className={`flex-shrink-0 h-11 w-11 rounded-xl flex items-center justify-center transition-colors ${
                        isActive
                          ? "bg-[#72B3E4]/20 text-[#72B3E4]"
                          : "bg-[#72B3E4]/15 text-[#055794]"
                      }`}
                    >
                      <Icon size={20} />
                    </span>
                    <span className="flex-1 font-heading font-semibold text-[15px] lg:text-base">
                      {s.title}
                    </span>
                    <ChevronRight
                      size={18}
                      className={`flex-shrink-0 transition-transform ${
                        isActive
                          ? "text-[#72B3E4] translate-x-1"
                          : "text-[#7F7F7F]"
                      }`}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab content */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35 }}
                className="relative h-full"
                data-testid={`service-content-${active}`}
              >
                <div className="relative h-full rounded-3xl bg-white border border-slate-200 overflow-hidden shadow-[0_30px_60px_-30px_rgba(2,33,64,0.2)]">
                  <div className="p-8 lg:p-12">
                    <div className="flex items-center gap-3 mb-6">
                      <span className="text-xs uppercase tracking-[0.25em] text-[#055794] font-semibold">
                        Servicio · 0{active + 1} / 0{SERVICES.length}
                      </span>
                    </div>
                    <h3 className="font-heading text-3xl lg:text-4xl font-bold text-[#022140] mb-6 leading-tight">
                      {SERVICES[active].title}
                    </h3>
                    <p className="text-[16px] text-[#022140]/75 leading-relaxed mb-8">
                      {SERVICES[active].description}
                    </p>
                    <p className="text-xs uppercase tracking-[0.22em] text-[#055794] font-semibold mb-4">
                      Áreas clave
                    </p>
                    <ul className="space-y-4">
                      {SERVICES[active].highlights.map((item, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-[#72B3E4] hover:bg-[#72B3E4]/5 transition-all duration-300"
                          data-testid={`service-item-${active}-${idx}`}
                        >
                          <span className="flex-shrink-0 mt-1 h-6 w-6 rounded-md bg-[#055794] text-white text-xs font-bold flex items-center justify-center">
                            {idx + 1}
                          </span>
                          <span className="text-[15px] text-[#022140] leading-relaxed">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div
                    aria-hidden
                    className="absolute bottom-0 right-0 w-64 h-64 bg-[#72B3E4]/15 rounded-full blur-3xl -mb-20 -mr-20 pointer-events-none"
                  />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
